import { CloseOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import React, { Fragment, useState } from "react";
import { AlenDevice } from "src/interfaces/IAlenDevice";
import { ShadowField } from "src/services/alen/alenDeviceConfig/utils";
import { WYND_WHITE } from "src/styles";

const optionSorter = (a: string, b: string) => {
  const isANumber = Number(a);
  const isBNumber = Number(b);

  if (isANumber && isBNumber) {
    // Both are numbers, sort numerically
    return Number(a) - Number(b);
  }
  if (isANumber) {
    // Numbers come before strings
    return -1;
  }
  if (isBNumber) {
    // Strings come after numbers
    return 1;
  }
  // Both are strings, sort alphabetically
  return a.localeCompare(b);
};

export type AlenBulkActionOption = {
  standardName: ShadowField;
  options: string[];
};

export default function AlenBulkActionModal({
  open,
  onClose = () => {},
  options = [],
  onExecute = () => {},
  selectedDevices,
  setSelectedDeviceIds,
}: {
  open: boolean;
  onClose?: () => void;
  options?: AlenBulkActionOption[];
  onExecute?: (actions: { actionName: string; actionValue: string }) => void;
  selectedDevices: AlenDevice[];
  setSelectedDeviceIds: (ids: string[]) => void;
}) {
  const [selected, setSelected] = useState<
    | {
        actionName: string;
        actionValue: string;
      }
    | undefined
  >();
  const handleSelected = (actionName: string, actionValue: string) => {
    setSelected({ actionName, actionValue });
  };
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Bulk Action</DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          gap: 1,
          minWidth: "20rem",
          flexDirection: "column",
        }}
      >
        <Stack direction="row" rowGap={0.5} columnGap={1} flexWrap="wrap">
          {selectedDevices.map((device) => (
            <Box
              sx={{
                backgroundColor: WYND_WHITE,
                padding: "0 1rem",
                borderRadius: "1rem",
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexDirection: "row",
              }}
              key={device.id}
            >
              <Stack>
                <Typography fontWeight={600}>{device.name}</Typography>
                <Typography>{device.id}</Typography>
              </Stack>
              <CloseOutlined
                onClick={() => {
                  setSelectedDeviceIds(
                    selectedDevices
                      .filter((d) => d.id !== device.id)
                      .map((d) => d.id)
                  );
                }}
                sx={{ width: "1rem", height: "1rem", cursor: "pointer" }}
              />
            </Box>
          ))}
        </Stack>
        {options.map((option) => (
          <Fragment key={option.standardName}>
            <Typography>{option.standardName}</Typography>
            <Stack direction="row" spacing={1}>
              {[...option.options].sort(optionSorter).map((value) => (
                <Button
                  onClick={() => handleSelected(option.standardName, value)}
                  key={value}
                  variant={
                    selected?.actionName === option.standardName &&
                    selected.actionValue === value
                      ? "contained"
                      : "outlined"
                  }
                >
                  {value}
                </Button>
              ))}
            </Stack>
          </Fragment>
        ))}
      </DialogContent>
      <DialogActions>
        <Button color="danger" onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={() => {
            if (selected) {
              onExecute(selected);
              onClose();
            }
          }}
        >
          Execute
        </Button>
      </DialogActions>
    </Dialog>
  );
}
